import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import PrintIcon from '@mui/icons-material/Print';
import Autocomplete from '@mui/material/Autocomplete';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  DATE_FORMAT_STRING,
  getDateOnly,
  formatDate,
  formatCurrency,
} from '#common/utils';
import {
  useGetCustomerReportMutation,
} from '#services/order';
import { useGetAllCheckoutAsUsersQuery } from '#services/user';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';

import CustomerReportTable from './CustomerReportTable';

interface CustomerUser {
  id?: string | number;
  username?: string;
  code?: string;
  isCustomer?: boolean;
  createdDate?: string;
  [key: string]: unknown;
}

interface ComboOption {
  label: string;
  data: CustomerUser;
}

interface CustomerReportRow {
  productId?: string | number;
  style?: string;
  variant?: string;
  qty?: number;
  totalPrice?: number;
  relatedOrders?: Array<string | number>;
  [key: string]: unknown;
}

interface CustomerReportItem {
  rows: EnhancedTableRow[];
  subtotal: number;
  collectionName: string;
}

interface ReportData {
  reports: CustomerReportItem[];
  total: number;
  createdDate: Date | string;
}

export default function CustomerReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const [getReport] = useGetCustomerReportMutation();
  const [reportData, setReportData] = useState<ReportData>({
    reports: [],
    total: 0,
    createdDate: new Date(),
  });

  const [selectedCustomer, setSelectedCustomer] = useState<CustomerUser | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(() => {
    const p = new URLSearchParams(location.search);
    const ms = p.get('dateFrom');
    return ms ? new Date(parseInt(ms, 10)) : new Date(getDateOnly().setDate(1));
  });
  const [dateTo, setDateTo] = useState<Date | null>(() => {
    const p = new URLSearchParams(location.search);
    const ms = p.get('dateTo');
    return ms ? new Date(parseInt(ms, 10)) : getDateOnly();
  });

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  // Fetch all customers independently of date range
  const { data: allCheckoutUsers } = useGetAllCheckoutAsUsersQuery(undefined) as {
    data?: CustomerUser[];
  };

  // Filter to show only customers and prepare options for autocomplete
  const customerOptions: ComboOption[] = [];
  if (allCheckoutUsers) {
    const customers = allCheckoutUsers.filter((user) => user.isCustomer);
    customers.forEach((customer) => {
      customerOptions.push({
        label: `${customer.username} (${customer.code})`,
        data: customer,
      });
    });
  }

  // Sync date range from URL params whenever the query string changes; reset to defaults when absent
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateFromMs = params.get('dateFrom');
    const dateToMs = params.get('dateTo');
    setDateFrom(dateFromMs ? new Date(parseInt(dateFromMs, 10)) : new Date(getDateOnly().setDate(1)));
    setDateTo(dateToMs ? new Date(parseInt(dateToMs, 10)) : getDateOnly());
  }, [location.search]);

  // Pre-select customer from URL param (e.g. when navigating from User Detail or dashboard)
  useEffect(() => {
    if (!allCheckoutUsers) return;
    const params = new URLSearchParams(location.search);
    const customerId = params.get('customerId');
    if (!customerId) {
      setSelectedCustomer(null);
      return;
    }
    const match = allCheckoutUsers.find((u) => String(u.id) === customerId);
    setSelectedCustomer(match || null);
  }, [allCheckoutUsers, location.search]);

  let dateFromMinDate = new Date();
  if (selectedCustomer) {
    dateFromMinDate = new Date(selectedCustomer.createdDate as string);
  }

  // Fetch report when customer or date range changes
  useEffect(() => {
    if (!dateFrom || !dateTo) {
      return;
    }

    const fetchReport = async () => {
      const queryOptions: { dateFrom: number; dateTo: number; customerId?: string | number | undefined } = {
        dateFrom: dateFrom.getTime(),
        dateTo: dateTo.getTime(),
      };

      if (selectedCustomer) {
        queryOptions.customerId = selectedCustomer.id;
      }

      try {
        const res = await getReport(queryOptions);
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        if (hasError) {
          return;
        }

        setReportData((res as { data: ReportData }).data);
      } catch (error) {
        console.error('Error fetching customer report:', error);
      }
    };

    void fetchReport();
  }, [selectedCustomer, dateFrom, dateTo, getReport, simpleSnackbar]);

  const handleReportExport = () => {
    if (!selectedCustomer || !dateFrom || !dateTo) {
      return;
    }

    const customerId = selectedCustomer.id;
    const exportUrl = `/api/export/customerReport?dateFrom=${dateFrom.getTime()}&dateTo=${dateTo.getTime()}&customerId=${customerId}`;
    window.open(exportUrl, '_blank');
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Customer Report
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <Autocomplete
              disablePortal
              id="customer-selector"
              options={customerOptions}
              fullWidth
              size="small"
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search a customer to start"
                  placeholder="Search customers..."
                  error={!selectedCustomer}
                />
              )}
              onChange={(event, value) => {
                const customer = value ? value.data : null;
                setSelectedCustomer(customer);
              }}
              value={customerOptions.find((option) => option.data?.id === selectedCustomer?.id) || null}
            />
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
          >
            From:&nbsp;
            <DesktopDatePicker
              format={DATE_FORMAT_STRING}
              name="dateRangeFrom"
              minDate={dateFromMinDate}
              value={dateFrom}
              maxDate={dateTo ?? new Date()}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(d) => {
                setDateFrom(d);
                if (!dateTo) {
                  setDateTo(new Date());
                }
              }}
            />
            &nbsp;To:&nbsp;
            <DesktopDatePicker
              format={DATE_FORMAT_STRING}
              name="dateRangeTo"
              value={dateTo}
              minDate={dateFrom ?? dateFromMinDate}
              maxDate={new Date()}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(d) => {
                setDateTo(d);
                // Backfill From to the start of the selected month, mirroring the page
                // default, so the range never ends up inverted
                if (!dateFrom && d) {
                  setDateFrom(new Date(d.getFullYear(), d.getMonth(), 1));
                }
              }}
            />

          </LocalizationProvider>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box />

        <Box>
          <Button
            variant="text"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <ButtonGroup variant="outlined">
            <Button
              onClick={handleReportExport}
            >
              Export
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {
        reportData.reports.map((report, idx) => {
          const tableHeaders: EnhancedTableProps['headers'] = [{
            id: 'style',
            label: 'Style',
            renderer: (row) => {
              const reportRow = row as CustomerReportRow;
              const url = `/shop/add?productId=${reportRow.productId}`;
              return (
                <Link
                  href={url}
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate(url);
                  }}
                  sx={{ textDecoration: 'inherit' }}
                >
                  {reportRow.style}
                </Link>
              );
            },
          }, {
            id: 'variant',
            label: 'Variant',
          }, {
            id: 'qty',
            label: 'QTY',
          }, {
            id: 'totalPrice',
            label: 'Total Price',
            renderer: (row) => formatCurrency(
              (row as CustomerReportRow).totalPrice as unknown as Parameters<typeof formatCurrency>[0],
            ),
          }, {
            id: 'relatedOrders',
            label: 'Orders',
            renderer: (row) => {
              const { relatedOrders } = row as CustomerReportRow;
              return (relatedOrders || []).map((orderId) => {
                const url = `/management/order/detail?orderId=${orderId}`;
                return (
                  <Link
                    key={orderId}
                    href={url}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(url);
                    }}
                    sx={{ textDecoration: 'inherit', mr: 1 }}
                  >
                    {orderId}
                  </Link>
                );
              });
            },
          }];

          const tableProps: Omit<EnhancedTableProps, 'rows'> = {
            name: 'customer-report-table',
            headers: tableHeaders,
            pinnedColumns: {
              left: ['style', 'variant'],
            },
          };

          return (
            <CustomerReportTable
              key={idx}
              report={report}
              tableProps={tableProps}
            />
          );
        })
      }

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box>
          Report Date:&nbsp;
          {formatDate(reportData.createdDate)}
        </Box>
        <Box>
          <b>Total</b>
          :
          {' '}
          <span data-testid="total">{formatCurrency(reportData.total)}</span>
        </Box>
      </Box>

    </>
  );
}
